// src/App.js

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import MeuPerfil from './pages/MeuPerfil';
import CentralAjuda from './pages/CentralAjuda'; // Added import

// CONTEXTOS E HOOKS
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ModalProvider } from './context/ModalContext';
import { NotificationProvider } from './context/NotificationContext';

// PÁGINAS GENÉRICAS E DE LAYOUT
import Spinner from './components/common/Spinner/Spinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import AvisoNovaVersao from './components/common/AvisoNovaVersao';
import OnboardingTour from './components/common/OnboardingTour/OnboardingTour';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import RecuperarSenha from './pages/RecuperarSenha';
import LoginComLink from './pages/LoginComLink';
import SolicitarLinkAcesso from './pages/SolicitarLinkAcesso';
import DiagnosticoPublico from './pages/DiagnosticoPublico';
import QuestoesPublicas from './pages/QuestoesPublicas';
import Dashboard from './pages/Dashboard';
import RedeFederal from './pages/RedeFederal';
import RedeFederalInstituicao from './pages/RedeFederalInstituicao';
import ProtectedRoute from './components/ProtectedRoute';

import { isMembroColegiado } from './utils/perfilUtils';

const RankingOrganizacoes = React.lazy(() => import('./components/views/admin_master/RankingOrganizacoes'));
const RespostaQuestao = React.lazy(() => import('./pages/RespostaQuestao'));


// LAYOUTS DE PAINEL
const AdminMasterLayout = React.lazy(() => import('./components/layouts/AdminMasterLayout'));
const AdminOrgLayout = React.lazy(() => import('./components/layouts/AdminOrgLayout'));
const ArticuladorLayout = React.lazy(() => import('./components/layouts/ArticuladorLayout'));
const AltaGestaoLayout = React.lazy(() => import('./components/layouts/AltaGestaoLayout'));

const ArticuladorOrgDashboard = React.lazy(() => import('./components/views/ArticuladorOrgDashboard'));
const MainLayout = React.lazy(() => import('./components/layouts/MainLayout'));

// PÁGINAS DO ADM MASTER
const DashboardGlobal = React.lazy(() => import('./components/views/admin_master/DashboardGlobal'));
const GestaoOrganizacoes = React.lazy(() => import('./components/views/admin_master/GestaoOrganizacoes'));
const VisualizacaoSetores = React.lazy(() => import('./components/views/admin_master/VisualizacaoSetores'));
const GestaoQuestoes = React.lazy(() => import('./components/views/admin_master/GestaoQuestoes'));
const QuestoesArquivadas = React.lazy(() => import('./components/views/admin_master/QuestoesArquivadas'));
const GestaoUsuarios = React.lazy(() => import('./components/views/admin_master/GestaoUsuarios'));
const AuditLogs = React.lazy(() => import('./components/views/admin_master/AuditLogs'));


// PÁGINAS DO ADM.ORG
const AdminOrgView = React.lazy(() => import('./components/views/AdminOrgView'));
const PriorizacaoMatriz = React.lazy(() => import('./components/views/PriorizacaoMatriz'));
const PriorizacaoColegiadaOrg = React.lazy(() => import('./pages/PriorizacaoColegiadaOrg'));
const PriorizacaoColegiadaGestao = React.lazy(() => import('./pages/PriorizacaoColegiadaGestao'));
const SobreMagoIesgoGestao = React.lazy(() => import('./pages/SobreMagoIesgoGestao'));
const GestaoUsuariosOrg = React.lazy(() => import('./pages/GestaoUsuariosOrg'));
const AtribuirQuestoes = React.lazy(() => import('./pages/AtribuirQuestoes'));
const QuestoesOrg = React.lazy(() => import('./pages/QuestoesOrg'));
const PendenciasArticuladores = React.lazy(() => import('./pages/PendenciasArticuladores'));
const AuditLogsOrg = React.lazy(() => import('./pages/AuditLogsOrg'));
const CentralEvidencias = React.lazy(() => import('./pages/CentralEvidencias'));

// PÁGINAS DO ARTICULADOR
const ArticuladorView = React.lazy(() => import('./components/views/ArticuladorView'));
const DashboardArticulador = React.lazy(() => import('./components/dashboard/DashboardArticulador'));
const ExportarDadosMaster = React.lazy(() => import('./components/views/ExportarDadosMaster'));
const ExportarDadosOrg = React.lazy(() => import('./components/views/ExportarDadosOrg'));
const ExportarDadosArticulador = React.lazy(() => import('./components/views/ExportarDadosArticulador'));
const DiagnosticoPublicoArticulador = React.lazy(() => import('./components/views/DiagnosticoPublicoArticulador'));

// --- COMPONENTES DE LÓGICA DE ROTA ---

// Rota para proteger páginas que só podem ser vistas por usuários logados
// Rota para proteger páginas que só podem ser vistas por usuários deslogados
const PublicRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  if (loading) return <Spinner text="A carregar..." fullScreen />;

  if (currentUser) return <Navigate to="/dashboard" replace />;
  return children;
};

// --- DEFINIÇÃO DE TODAS AS ROTAS DA APLICAÇÃO ---
const AppRoutes = () => {
  return (
    <Routes>
      {/* Páginas públicas */}
      <Route path="/diagnostico/:orgId" element={<DiagnosticoPublico />} />
      <Route path="/diagnostico/:orgId/questoes" element={<QuestoesPublicas />} />
      <Route path="/rede-federal" element={<RedeFederal />} />
      <Route path="/rede-federal/:slug" element={<RedeFederalInstituicao />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/recuperar-senha" element={<PublicRoute><RecuperarSenha /></PublicRoute>} />
      <Route path="/entrar-link" element={<LoginComLink />} />
      <Route path="/acessar-por-link" element={<PublicRoute><SolicitarLinkAcesso /></PublicRoute>} />

      <Route element={<MainLayout />}>
        <Route path="/ajuda" element={<CentralAjuda />} />
        <Route path="/meu-perfil" element={<ProtectedRoute><MeuPerfil /></ProtectedRoute>} />
      </Route>

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* ===== ADM MASTER ===== */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredProfile="ADM MASTER">
            <AdminMasterLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardGlobal />} />
        <Route path="organizacoes" element={<GestaoOrganizacoes />} />
        <Route path="segmentacao" element={<VisualizacaoSetores />} />
        <Route path="questoes" element={<GestaoQuestoes />} />
        <Route path="questoes-arquivadas" element={<QuestoesArquivadas />} />
        <Route path="usuarios" element={<GestaoUsuarios />} />
        {/* ✅ ROTA DO RANKING CORRIGIDA E MOVIDA PARA CÁ */}
        <Route path="ranking" element={<RankingOrganizacoes />} />
        <Route path="logs" element={<AuditLogs />} />
        <Route path="exportar" element={<ExportarDadosMaster />} />
      </Route>

      {/* ===== ADM ORG ===== */}
      <Route
        path="/adm-org"
        element={
          <ProtectedRoute requiredProfile="ADM.ORG">
            <AdminOrgLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="painel" replace />} />
        <Route path="painel" element={<AdminOrgView />} />
        <Route path="usuarios" element={<GestaoUsuariosOrg />} />
        <Route path="atribuir-questoes" element={<AtribuirQuestoes />} />
        <Route path="questoes" element={<QuestoesOrg />} />
        <Route path="priorizacao" element={<PriorizacaoMatriz />} />
        <Route path="priorizacao-colegiada" element={<PriorizacaoColegiadaOrg />} />
        <Route path="questao/:questionId" element={<RespostaQuestao />} />
        <Route path="pendencias" element={<PendenciasArticuladores />} />
        <Route path="exportar" element={<ExportarDadosOrg />} />
        <Route path="logs" element={<AuditLogsOrg />} />
        <Route path="evidencias" element={<CentralEvidencias />} />
      </Route>

      {/* ===== ARTICULADOR ===== */}
      <Route
        path="/articulador"
        element={
          <ProtectedRoute requiredProfile="ARTICULADOR">
            <ArticuladorLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardArticulador />} />
        <Route path="painel-organizacao" element={<ArticuladorOrgDashboard />} />
        <Route path="diagnostico" element={<DiagnosticoPublicoArticulador />} />
        <Route path="questoes" element={<ArticuladorView />} />
        <Route path="questao/:questionId" element={<RespostaQuestao />} />
        <Route path="exportar" element={<ExportarDadosArticulador />} />
        <Route path="evidencias" element={<CentralEvidencias />} />
      </Route>

      {/* ===== ALTA GESTAO / PRIORIZACAO COLEGIADA ===== */}
      <Route
        path="/gestao"
        element={
          <ProtectedRoute isAllowed={(u) => isMembroColegiado(u)}>
            <AltaGestaoLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="priorizacao-colegiada" replace />} />
        <Route path="priorizacao-colegiada" element={<PriorizacaoColegiadaGestao />} />
        <Route path="sobre" element={<SobreMagoIesgoGestao />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>

  );
};

// --- COMPONENTE PRINCIPAL ---
function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
            <ModalProvider>
              <ErrorBoundary>
                <OnboardingTour />
                {/* Fora do ErrorBoundary de rota nao: o aviso precisa
                    sobreviver a qualquer tela, inclusive quando a versao velha
                    for justamente a que esta quebrando. */}
                <AvisoNovaVersao />
                <AppRoutes />
              </ErrorBoundary>
            </ModalProvider>
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
